import { useNavigate } from "react-router-dom";
import Table from "src/components/Table/Table";
import useRememberTable from "src/hooks/useRememberTable";
import { Grid } from "@mui/material";
import * as Yup from "yup";
import { tableControls } from "./const";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  QuerySnapshot,
  DocumentData,
  Timestamp,
} from "firebase/firestore";
import { authClient, db } from "src/api/firebase";
import { FilterCondition } from "src/models/filter";
import { GridRowModel } from "@mui/x-data-grid";
import useRevertDialog from "src/components/revertDialog/RevertDialog";

interface TradeData extends DocumentData {
  id: string;
  createdAt?: Date | Timestamp;
  transactionStatus: string;
  transactionType: string;
}

interface TradeRow extends TradeData {
  _status?: "new" | "modified" | "existing";
  createdAt?: Date;
}

type RowStatusMap = Map<string, "new" | "modified">;

const TABLE_COLUMNS = tableControls.getTableColumns();
const FILTER_CONFIG = tableControls.getFilterConfig();
const TABLE_NAME = "Open Trades";

/**
 * Checks if a single row matches all the provided filter conditions.
 * @param row The data row to check.
 * @param conditions An array of filter conditions to apply.
 * @returns True if the row matches all conditions, false otherwise.
 */
const applyFilters = (
  row: TradeRow,
  conditions: FilterCondition[]
): boolean => {
  if (!conditions || conditions.length === 0) {
    return true;
  }

  for (const condition of conditions) {
    const { field, operator, value } = condition;
    const rowValue = row[field];

    if (rowValue === null || typeof rowValue === "undefined") {
      return false;
    }

    const filterValueString = String(value).toLowerCase();
    const rowValueString = String(rowValue).toLowerCase();

    let match = false;
    switch (operator) {
      case "==":
        match = rowValueString === filterValueString;
        // if comparing dates: match = rowValue instanceof Date && value instanceof Date && rowValue.getTime() === value.getTime();
        break;
      case "!=":
        match = rowValueString !== filterValueString;
        break;
      case ">":
        match = Number(rowValue) > Number(value);
        break;
      case "<":
        match = Number(rowValue) < Number(value);
        break;
      case ">=":
        match = Number(rowValue) >= Number(value);
        break;
      case "<=":
        match = Number(rowValue) <= Number(value);
        break;
      default:
        console.warn(`Unsupported filter operator: ${operator}`);
        match = true;
    }

    if (!match) {
      return false;
    }
  }

  return true;
};

export default function ListOpenTrades() {
  const [pendingTrades, setPendingTrades] = useState<TradeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchFlag, setRefetchFlag] = useState(0);
  const user = authClient.currentUser;
  const { openDialog, closeDialog, DialogComponent } = useRevertDialog();

  const previousDocIds = useRef<Set<string>>(new Set());
  const isInitialLoad = useRef(true);

  const refetch = useCallback(() => {
    // reset initial load flag on manual refetch
    // otherwise new rows on refetch might not highlight
    isInitialLoad.current = true;
    previousDocIds.current = new Set();
    setRefetchFlag((p) => p + 1);
  }, []);

  const {
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    filter,
  } = useRememberTable({ tableName: TABLE_NAME });
  const navigate = useNavigate();

  const filterConditions = useMemo(
    () => tableControls.buildFilterConditions(filter),
    [filter]
  );

  const onPlayerClick = (playerId: string) => {
    navigate(`/player-detail-dashboard/${playerId}`);
  };

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setPendingTrades([]);
      setError("User not authenticated.");
      return;
    }

    setLoading(true);
    setError(null);

    const transactionsColRef = collection(db, "transactions");

    // Base query
    const q = query(
      transactionsColRef,
      where("transactionStatus", "==", "PENDING"),
      where("transactionType", "==", "TRADE"),
      orderBy("createdAt", "desc")
    );

    console.log("Setting up Firestore listener...");

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot: QuerySnapshot<DocumentData>) => {
        console.log("Snapshot received. Processing changes...");
        const currentDocIds = new Set<string>();
        const changesMap: RowStatusMap = new Map();

        // process changes to identify new/modified rows *since the last snapshot*
        querySnapshot.docChanges().forEach((change) => {
          const docId = change.doc.id;
          if (change.type === "added") {
            if (!isInitialLoad.current && !previousDocIds.current.has(docId)) {
              changesMap.set(docId, "new");
            } else {
              // modified
            }
          } else if (change.type === "modified") {
            console.log(`Modified document detected: ${docId}`);
            changesMap.set(docId, "modified");
          } else if (change.type === "removed") {
            console.log(`Removed document detected: ${docId}`);
          }
        });

        const trades: TradeRow[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as TradeData;
          const docId = doc.id;
          currentDocIds.add(docId);

          trades.push({
            ...data,
            id: docId,
            createdAt:
              data.createdAt instanceof Timestamp
                ? data.createdAt.toDate()
                : data.createdAt,
            _status: changesMap.get(docId) || "existing",
          });
        });

        setPendingTrades(trades);
        setLoading(false);
        setError(null);

        previousDocIds.current = currentDocIds;

        if (isInitialLoad.current) {
          isInitialLoad.current = false;
          console.log("Initial load complete.");
        }
      },
      (err) => {
        console.error("Error fetching pending trades:", err);
        setError("Failed to load pending trades. Please try again later.");
        setLoading(false);
        setPendingTrades([]);
        previousDocIds.current = new Set();
        isInitialLoad.current = true;
      }
    );

    return () => {
      console.log("Unsubscribing from pending trades listener");
      unsubscribe();

      previousDocIds.current = new Set();
      isInitialLoad.current = true;
    };
  }, [refetchFlag, user]);

  const filteredTrades = useMemo(() => {
    return pendingTrades.filter((trade) =>
      applyFilters(trade, filterConditions)
    );
  }, [pendingTrades, filterConditions]);

  const onRevertCellClick = (
    params: {
      id: string | number;
      field: string;
      row: GridRowModel<TradeRow>;
    },
    event: any
  ) => {
    event.stopPropagation();
    openDialog({
      userId: params.row.userId,
      tradeId: params.row.id,
    });
  };

  return (
    <>
      {error && (
        <Grid item xs={12} style={{ color: "red", margin: "10px 0" }}>
          Error: {error}
        </Grid>
      )}
      <Table
        rows={filteredTrades}
        columns={TABLE_COLUMNS}
        paginationMode="client"
        filterMode="custom"
        pageSizeOptions={[5, 10, 20]}
        loading={loading}
        refetch={refetch}
        isFetching={loading}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        sortModel={sortModel}
        setSortModel={setSortModel}
        totalRows={filteredTrades.length}
        error={!!error}
        onRowClick={({ row }) => onPlayerClick(row.userId)}
        filterConfig={{
          tableName: TABLE_NAME,
          schema: Yup.object().shape({
            id: Yup.string().min(1, "Too Short!"),
            userId: Yup.string().min(1, "Too Short!"),
          }),
          config: FILTER_CONFIG,
        }}
        onCellClick={onRevertCellClick}
      />
      <DialogComponent />
    </>
  );
}
